// Guest token mechanism (issue #196).
//
// The gateway rejects every non-/api/auth/*, non-actuator, non-swagger,
// non-/webhooks/ request that carries neither `Authorization: Bearer <token>`
// (logged-in users) nor `X-Guest-Token: <jwt>` (unauthenticated clients) with
// HTTP 401 { "error": "guest_token_required" }.
//
// This module mints, caches, and refreshes a guest token so tokenless calls
// (translations, public category/terms/offering lookups) keep working.

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const STORAGE_KEY = 'garagat_guest_token';
const EXPIRY_KEY = 'garagat_guest_token_exp';

// Default lifetime when the server omits expires_in (seconds; 24h).
const DEFAULT_EXPIRES_IN = 86400;
// Refresh slightly before actual expiry so an in-flight request never races it.
const EXPIRY_SKEW_MS = 60_000;

interface GuestTokenResponse {
  guest_token: string;
  expires_in?: number;
}

// Single in-flight promise so a cold start firing N parallel tokenless calls
// only hits /api/auth/guest once.
let inFlight: Promise<string> | null = null;

export function clearGuestToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(EXPIRY_KEY);
  } catch {
    // localStorage unavailable (e.g. private mode) — nothing to clear.
  }
}

function readCachedToken(): string | null {
  try {
    const token = localStorage.getItem(STORAGE_KEY);
    const expRaw = localStorage.getItem(EXPIRY_KEY);
    if (!token || !expRaw) return null;
    const exp = Number(expRaw);
    if (!Number.isFinite(exp)) return null;
    // Treat as expired if within the skew window of its expiry.
    if (Date.now() >= exp - EXPIRY_SKEW_MS) return null;
    return token;
  } catch {
    return null;
  }
}

async function fetchNew(): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/auth/guest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to mint guest token: HTTP ${response.status}`);
  }
  const data = (await response.json()) as GuestTokenResponse;
  if (!data || !data.guest_token) {
    throw new Error('Guest token response missing guest_token');
  }
  const expiresIn =
    typeof data.expires_in === 'number' && data.expires_in > 0
      ? data.expires_in
      : DEFAULT_EXPIRES_IN;
  try {
    localStorage.setItem(STORAGE_KEY, data.guest_token);
    localStorage.setItem(EXPIRY_KEY, String(Date.now() + expiresIn * 1000));
  } catch {
    // localStorage unavailable — token still returned for this session.
  }
  return data.guest_token;
}

export async function getGuestToken(): Promise<string> {
  const cached = readCachedToken();
  if (cached) return cached;

  if (inFlight) return inFlight;

  inFlight = fetchNew();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}
